import { Plus, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { AddLeadDialog } from '@/modules/marketing/components/leads/AddLeadDialog'
import { Button } from '@/shared/components/ui/button'
import CustomDateRangePicker from '@/shared/components/ui/CustomDateRangePicker'
import DashboardPeriodFilter from '@/shared/components/ui/DashboardPeriodFilter'
import { PERIOD_OPTIONS } from '@/shared/constants/dashboardPeriod'

import EmailModal from '../components/leads/EmailModal'
import SalesLeadsTable from '../components/leads/SalesLeadsTable'
import SourceFilter from '../components/leads/SourceFilter'

export default function SalesLeadsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const sourceParam = searchParams.get('source') || 'all'
  const statusParam = searchParams.get('status') || ''
  const warmthParam = searchParams.get('leadWarmth') || ''
  const periodParam = searchParams.get('period') || 'all'
  const startDateParam = searchParams.get('startDate') || ''
  const endDateParam = searchParams.get('endDate') || ''

  const [isEmailOpen, setIsEmailOpen] = useState(false)
  const [selectedLead, setSelectedLead] = useState(null)

  const [source, setSource] = useState(sourceParam)
  const [period, setPeriod] = useState(periodParam)
  const [customRange, setCustomRange] = useState({
    startDate: startDateParam,
    endDate: endDateParam,
  })
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (sourceParam) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSource(sourceParam)
    }
  }, [sourceParam])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPeriod(periodParam)
    setCustomRange({
      startDate: startDateParam,
      endDate: endDateParam,
    })
  }, [periodParam, startDateParam, endDateParam])

  const handlePeriodChange = (val) => {
    setPeriod(val)
    const updated = new URLSearchParams(searchParams)
    if (val === 'all') {
      updated.delete('period')
      updated.delete('startDate')
      updated.delete('endDate')
    } else {
      updated.set('period', val)
      if (val !== 'custom') {
        updated.delete('startDate')
        updated.delete('endDate')
      }
    }
    setSearchParams(updated)
  }

  const handleCustomRangeChange = (range) => {
    setCustomRange(range)
    const updated = new URLSearchParams(searchParams)
    updated.set('period', 'custom')
    if (range.startDate) updated.set('startDate', range.startDate)
    else updated.delete('startDate')
    if (range.endDate) updated.set('endDate', range.endDate)
    else updated.delete('endDate')
    setSearchParams(updated)
  }

  const handleEmail = (lead) => {
    setSelectedLead(lead)
    setIsEmailOpen(true)
  }

  const clearFilter = (key) => {
    const updated = new URLSearchParams(searchParams)
    updated.delete(key)
    if (key === 'period') {
      updated.delete('startDate')
      updated.delete('endDate')
      setPeriod('all')
      setCustomRange({ startDate: '', endDate: '' })
    }
    setSearchParams(updated)
    if (key === 'source') {
      setSource('all')
    }
  }

  const clearAllFilters = () => {
    const updated = new URLSearchParams(searchParams)
    updated.delete('status')
    updated.delete('leadWarmth')
    updated.delete('source')
    updated.delete('period')
    updated.delete('startDate')
    updated.delete('endDate')
    setSearchParams(updated)
    setSource('all')
    setPeriod('all')
    setCustomRange({ startDate: '', endDate: '' })
  }

  const hasActiveFilters = Boolean(
    statusParam ||
    warmthParam ||
    (sourceParam && sourceParam !== 'all') ||
    (periodParam && periodParam !== 'all'),
  )

  const selectedPeriodOption = PERIOD_OPTIONS.find(
    (opt) => opt.value === periodParam,
  )

  return (
    <>
      <div className="flex w-full min-w-0 flex-col gap-4 overflow-x-hidden sm:gap-6">
        {/* Header */}
        <div className="w-full rounded-3xl border bg-white p-5 shadow-sm sm:p-7">
          <div className="flex min-w-0 flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
            {/* Title */}
            <div className="min-w-0">
              <h1 className="text-3xl font-bold sm:text-4xl">Leads</h1>

              <p className="mt-2 max-w-[220px] text-sm text-muted-foreground sm:max-w-none sm:text-base">
                Manage your assigned leads.
              </p>
            </div>

            {/* Actions */}
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:gap-3">
              <Button
                size="lg"
                className="w-full rounded-full bg-[#0F172A] text-white hover:bg-[#0F172A] sm:w-auto"
                onClick={() => setOpen(true)}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Lead
              </Button>

              <div className="w-full sm:w-auto">
                <SourceFilter
                  value={source}
                  onChange={(val) => {
                    setSource(val)
                    const updated = new URLSearchParams(searchParams)
                    if (val === 'all') updated.delete('source')
                    else updated.set('source', val)
                    setSearchParams(updated)
                  }}
                />
              </div>

              {period === 'custom' && (
                <div className="w-full sm:w-auto">
                  <CustomDateRangePicker
                    startDate={customRange.startDate}
                    endDate={customRange.endDate}
                    onChange={handleCustomRangeChange}
                  />
                </div>
              )}

              <div className="w-full sm:w-auto">
                <DashboardPeriodFilter
                  value={period}
                  onChange={handlePeriodChange}
                />
              </div>
            </div>
          </div>

          {/* Active Filter Chips */}
          {hasActiveFilters && (
            <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
              <span className="text-xs font-semibold text-slate-500">
                Filtered by:
              </span>
              {periodParam && periodParam !== 'all' && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                  Period:{' '}
                  {selectedPeriodOption?.label ||
                    periodParam.toUpperCase().replace(/_/g, ' ')}
                  {periodParam === 'custom' &&
                    startDateParam &&
                    endDateParam && (
                      <span className="text-[11px] font-normal text-emerald-600">
                        ({startDateParam} to {endDateParam})
                      </span>
                    )}
                  <button
                    type="button"
                    onClick={() => clearFilter('period')}
                    className="hover:text-emerald-950"
                  >
                    <X className="size-3" />
                  </button>
                </span>
              )}
              {statusParam && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
                  Status: {statusParam.toUpperCase().replace(/_/g, ' ')}
                  <button
                    type="button"
                    onClick={() => clearFilter('status')}
                    className="hover:text-blue-950"
                  >
                    <X className="size-3" />
                  </button>
                </span>
              )}
              {warmthParam && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
                  Warmth: {warmthParam.toUpperCase()}
                  <button
                    type="button"
                    onClick={() => clearFilter('leadWarmth')}
                    className="hover:text-amber-950"
                  >
                    <X className="size-3" />
                  </button>
                </span>
              )}
              {sourceParam && sourceParam !== 'all' && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-purple-50 px-3 py-1 text-xs font-semibold text-purple-700">
                  Source: {sourceParam.toUpperCase()}
                  <button
                    type="button"
                    onClick={() => clearFilter('source')}
                    className="hover:text-purple-950"
                  >
                    <X className="size-3" />
                  </button>
                </span>
              )}
              <button
                type="button"
                onClick={clearAllFilters}
                className="text-xs font-medium text-slate-500 underline hover:text-slate-800"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>

        {/* Leads Table */}
        <div className="w-full min-w-0 overflow-hidden rounded-3xl border bg-white p-3 shadow-sm sm:p-6">
          <SalesLeadsTable
            source={source}
            status={statusParam}
            leadWarmth={warmthParam}
            period={period}
            customRange={customRange}
            onEmail={handleEmail}
          />
        </div>
      </div>

      <EmailModal
        key={selectedLead?._id}
        open={isEmailOpen}
        onOpenChange={setIsEmailOpen}
        lead={selectedLead}
      />

      <AddLeadDialog open={open} onOpenChange={setOpen} module="sales" />
    </>
  )
}
